import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface ColumnDetectionControlsProps {
  manualColumnSelection: boolean;
  hasSeparateDateAndTime: boolean;
  onManualSelectionChange: (value: boolean) => void;
  onHasSeparateDateAndTimeChange: (value: boolean) => void;
  onReDetectColumns: () => void;
}

const ColumnDetectionControls: React.FC<ColumnDetectionControlsProps> = ({
  manualColumnSelection,
  hasSeparateDateAndTime,
  onManualSelectionChange,
  onHasSeparateDateAndTimeChange,
  onReDetectColumns
}) => {
  const { t } = useTranslation('dataUpload');

  return (
    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-accent border border-primary/20 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <input
            type="checkbox"
            id="manualSelection"
            checked={manualColumnSelection}
            onChange={(e) => onManualSelectionChange(e.target.checked)}
            className="w-4 h-4 text-primary border-input rounded focus:ring-ring flex-shrink-0"
          />
          <label htmlFor="manualSelection" className="text-xs sm:text-sm font-medium text-foreground cursor-pointer">
            {t('columnSelection.manualMode')}
          </label>
          <InfoTooltip text={t('tooltips.manualColumnSelection')} />
        </div>

        {!manualColumnSelection && (
          <button
            type="button"
            onClick={onReDetectColumns}
            className="w-full sm:w-auto px-3 py-1.5 text-xs sm:text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-colors"
          >
            🔄 {t('columnSelection.reDetect')}
          </button>
        )}
      </div>

      {manualColumnSelection && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="columnFormat"
                checked={!hasSeparateDateAndTime}
                onChange={() => onHasSeparateDateAndTimeChange(false)}
                className="w-4 h-4 text-primary border-input focus:ring-ring flex-shrink-0"
              />
              <span className="text-xs sm:text-sm text-foreground">{t('columnSelection.singleDateTime')}</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="columnFormat"
                checked={hasSeparateDateAndTime}
                onChange={() => onHasSeparateDateAndTimeChange(true)}
                className="w-4 h-4 text-primary border-input focus:ring-ring flex-shrink-0"
              />
              <span className="text-xs sm:text-sm text-foreground">{t('columnSelection.separateDateTime')}</span>
            </label>
          </div>
        </div>
      )}

      {!manualColumnSelection && (
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-2">
          ℹ️ {t('columnSelection.autoDetectHint')}
        </p>
      )}
    </div>
  );
};

export default ColumnDetectionControls;
